import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, HelpCircle, ChevronDown, Play, Phone, MessageCircle, FileText, Gavel, ArrowRight, Menu } from 'lucide-react';
import SidebarNav from '../components/SidebarNav';
import './HelpPage.css';

const faqData = [
    {
        question: 'How is my route calculated?',
        answer: 'Our system uses real-time traffic data, vehicle weight, and height restrictions to calculate the most efficient and compliant route for your specific truck profile. We also factor in mandatory fuel stops and weigh stations.'
    },
    {
        question: 'What is the 30-minute break rule?',
        answer: 'The FMCSA requires a 30-minute break after 8 hours of cumulative driving time. Our app automatically triggers a countdown and alerts you 45 minutes before the break is mandatory to ensure you find a safe parking spot.'
    },
    {
        question: 'Syncing my ELD device',
        answer: 'Ensure your ELD hardware is plugged into the diagnostic port. In the app settings, select "Device Management" and tap "Connect". Ensure Bluetooth is enabled on your mobile device.'
    },
    {
        question: 'Updating my driver profile',
        answer: 'Navigate to the "Profile" section from the top navigation. You can update your CDL information, medical card expiration, and emergency contact details. Changes are synced with your fleet manager instantly.'
    }
];

const HelpPage = ({ currentPage, onNavigate, onSignOut, userProfile, onShowAuth }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [openFaq, setOpenFaq] = useState(0);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

    const filteredFaqs = faqData.filter(faq => 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="help-layout">
            <SidebarNav 
                currentPage={currentPage} 
                onNavigate={onNavigate}
                onSignOut={onSignOut}
                isMobileOpen={isMobileNavOpen}
                onCloseMobile={() => setIsMobileNavOpen(false)}
                userProfile={userProfile}
                onShowAuth={onShowAuth}
            />

            <main className="help-main">
                <div className="help-container">
                    {/* Header */}
                    <header className="help-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button 
                                className="mobile-menu-btn"
                                onClick={() => setIsMobileNavOpen(true)}
                            >
                                <Menu size={24} />
                            </button>
                            <h1>Help & Resources Center</h1>
                        </div>
                        <p>Get instant answers to your compliance, routing, and automation questions.</p>
                    </header>

                    {/* Search Bar */}
                    <div className="search-bar">
                        <Search size={20} />
                        <input 
                            type="text"
                            placeholder="Search for topics (e.g., Logbook, ELD, HOS)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="search-btn">Search</button>
                    </div>

                    {/* Content Grid */}
                    <div className="help-grid">
                        {/* Left Column - FAQs */}
                        <div className="help-left">
                            <h2 className="faq-title">
                                <HelpCircle size={20} />
                                Frequently Asked Questions
                            </h2>

                            <div className="faq-list">
                                {filteredFaqs.map((faq, index) => (
                                    <div 
                                        key={index} 
                                        className={`faq-item ${openFaq === index ? 'open' : ''}`}
                                    >
                                        <button 
                                            className="faq-question"
                                            onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                                        >
                                            <span>{faq.question}</span>
                                            <ChevronDown size={20} className="faq-chevron" />
                                        </button>
                                        <AnimatePresence>
                                            {openFaq === index && (
                                                <motion.div
                                                    className="faq-answer"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <p>{faq.answer}</p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>

                            {/* Video Tutorial Card */}
                            <div className="tutorial-card">
                                <div className="tutorial-thumbnail">
                                    <Play size={32} />
                                </div>
                                <div className="tutorial-content">
                                    <h4>New: Smart HOS Dashboard</h4>
                                    <p>Watch this 3-minute video to learn how to master the new compliance interface and avoid violations.</p>
                                    <button className="watch-link">
                                        Watch Tutorial <ArrowRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Resources */}
                        <div className="help-right">
                            {/* Compliance Central */}
                            <div className="compliance-card">
                                <div className="card-header">
                                    <Gavel size={20} />
                                    <h3>Compliance Central</h3>
                                </div>
                                <p>Stay up to date with official Department of Transportation regulations.</p>
                                <a href="https://www.fmcsa.dot.gov/regulations/hours-service" target="_blank" rel="noopener noreferrer" className="fmcsa-btn">
                                    FMCSA HOS Regulations
                                </a>
                            </div>

                            {/* Driver Support */}
                            <div className="support-card">
                                <h3>Driver Support</h3>
                                <div className="support-list">
                                    <div className="support-item">
                                        <div className="support-icon"><Phone size={18} /></div>
                                        <div>
                                            <p className="support-label">24/7 Hotline</p>
                                            <p className="support-value">+1 (800) SPOT-LOGS</p>
                                        </div>
                                    </div>
                                    <div className="support-item">
                                        <div className="support-icon"><MessageCircle size={18} /></div>
                                        <div>
                                            <p className="support-label">Live Chat</p>
                                            <p className="support-value online">Online - Avg. wait 2m</p>
                                        </div>
                                    </div>
                                    <div className="support-item">
                                        <div className="support-icon"><FileText size={18} /></div>
                                        <div>
                                            <p className="support-label">Driver Manual</p>
                                            <p className="support-value">PDF - 4.2 MB</p>
                                        </div>
                                    </div>
                                </div>
                                <button className="support-ticket-btn">Open Support Ticket</button>
                            </div>

                            {/* System Status */}
                            <div className="status-card">
                                <div className="status-indicator"></div>
                                <span>System Status</span>
                                <span className="status-text">All Systems Operational</span>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default HelpPage;